import { createContext, useContext, type ReactNode } from 'react'

import {
  getStoreCreator,
  type StoreConstructor,
  type StoreProps,
} from '@/utils/mobx/getStoreCreator'

/**
 * React provider factory for a MobX store: layers a context Provider + useStore on top of
 * `getStoreCreator`, so the store's create/prop-sync/subscribe lifecycle isn't duplicated.
 * Props (and their type) are inferred from the store constructor's first argument.
 */
export const getStoreProvider = <C extends StoreConstructor>(StoreClass: C) => {
  type T = InstanceType<C>
  type Props = StoreProps<C>

  const useCreateStore = getStoreCreator(StoreClass)
  const StoreContext = createContext<T | null>(null)

  const StoreProvider = ({
    children,
    // Auto-subscribe on mount (default). false defers it; flip to true later to subscribe.
    // Kept out of ...props so it never reaches the store.
    subscribe = true,
    ...props
  }: Props & { children: ReactNode; subscribe?: boolean }) => {
    const store = useCreateStore(props as never, { subscribe })

    return <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
  }

  const useStore = (): T => {
    const store = useContext(StoreContext)
    if (store === null) {
      throw new Error('useStore must be used within its StoreProvider')
    }
    return store
  }

  return { StoreProvider, useStore }
}
